'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  ArrowRight, ArrowLeft, CheckCircle, Landmark, User, Building2,
  FileText, Shield, Eye, EyeOff, ChevronRight, AlertCircle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';



// ─── Types ────────────────────────────────────────────────────────────────────
interface FormData {
  // Step 1 — Account type
  accountType: 'individual' | 'corporate' | '';
  // Step 2 — Personal details
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nationality: string;
  dateOfBirth: string;
  // Step 3 — Investor profile
  netWorth: string;
  annualIncome: string;
  investmentExperience: string;
  riskTolerance: string;
  // Step 4 — Password
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
  agreeRisk: boolean;
}

const STEPS = [
  { id: 1, label: 'Account Type', icon: Building2 },
  { id: 2, label: 'Personal Details', icon: User },
  { id: 3, label: 'Investor Profile', icon: FileText },
  { id: 4, label: 'Secure Access', icon: Shield },
];

const NATIONALITIES = ['United Arab Emirates', 'Saudi Arabia', 'Kuwait', 'Qatar', 'Bahrain', 'Oman', 'United Kingdom', 'United States', 'Singapore', 'India', 'Other'];
const NET_WORTH_OPTIONS = ['$500K – $1M', '$1M – $5M', '$5M – $25M', '$25M – $100M', '$100M+'];
const INCOME_OPTIONS = ['$100K – $200K', '$200K – $500K', '$500K – $1M', '$1M+'];
const EXPERIENCE_OPTIONS = ['< 2 years', '2–5 years', '5–10 years', '10+ years'];
const RISK_OPTIONS = ['Conservative', 'Moderate', 'Balanced', 'Growth', 'Aggressive'];

export default function OnboardingPage() {
  const router = useRouter();
  const { signUp } = useAuth();

  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState<FormData>({
    accountType: '', firstName: '', lastName: '', email: '', phone: '',
    nationality: '', dateOfBirth: '', netWorth: '', annualIncome: '',
    investmentExperience: '', riskTolerance: '', password: '', confirmPassword: '',
    agreeTerms: false, agreeRisk: false,
  });

  const update = (field: keyof FormData, value: string | boolean) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const canProceed = () => {
    if (step === 1) return form.accountType !== '';
    if (step === 2) return !!(form.firstName && form.lastName && form.email && form.nationality);
    if (step === 3) return !!(form.netWorth && form.annualIncome && form.riskTolerance);
    if (step === 4) return form.password.length >= 8 && form.password === form.confirmPassword && form.agreeTerms && form.agreeRisk;
    return false;
  };

  const handleSubmit = async () => {
    if (!canProceed()) return;
    setError('');
    setLoading(true);
    try {
      await signUp(form.email, form.password, {
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        role: 'investor',
        accountType: form.accountType,
      });
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-900/40 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Application Submitted</h1>
          <p className="text-[#94a3b8] mb-2">
            Thank you, <span className="text-white font-semibold">{form.firstName}</span>. Your onboarding application has been received.
          </p>
          <p className="text-sm text-[#64748b] mb-8">
            Our compliance team will review your KYC documentation within 1–2 business days. You&apos;ll receive an email at <span className="text-[#94a3b8]">{form.email}</span> once your account is approved.
          </p>
          <div className="p-4 rounded-xl bg-[#111827] border border-[#1e293b] mb-8 text-left">
            <div className="text-xs font-semibold text-[#64748b] uppercase tracking-widest mb-3">Next Steps</div>
            {[
              'Check your email for a verification link',
              'Upload identity documents when prompted',
              'Complete suitability assessment',
              'Gain access to live campaigns',
            ].map((s, i) => (
              <div key={s} className="flex items-center gap-3 py-2 border-b border-[#1e293b] last:border-0">
                <div className="w-5 h-5 rounded-full bg-emerald-900/40 border border-emerald-500/30 flex items-center justify-center text-[10px] font-bold text-emerald-400 flex-shrink-0">
                  {i + 1}
                </div>
                <span className="text-sm text-[#94a3b8]">{s}</span>
              </div>
            ))}
          </div>
          <button
            onClick={() => router.push('/login')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl transition-all">
            Sign In to Platform <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1e293b] bg-[#111827]/60 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/landing" className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
              <Landmark className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold text-white tracking-tight">CrowdVault<sup className="text-emerald-400 text-[10px] ml-0.5">™</sup></span>
          </Link>
          <div className="text-sm text-[#64748b]">
            Already have an account?{' '}
            <Link href="/login" className="text-emerald-400 hover:text-emerald-300 transition-colors font-medium">Sign in</Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          {/* Progress steps */}
          <div className="flex items-center justify-between mb-10">
            {STEPS.map((s, i) => {
              const StepIcon = s.icon;
              const isActive = step === s.id;
              const isDone = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex flex-col items-center gap-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all ${
                      isDone ? 'bg-emerald-500 border-emerald-500 text-white' : isActive ?'bg-emerald-900/40 border-emerald-500/50 text-emerald-400' :'bg-[#111827] border-[#1e293b] text-[#475569]'
                    }`}>
                      {isDone ? <CheckCircle className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                    </div>
                    <span className={`text-xs font-medium hidden sm:block ${isActive ? 'text-white' : isDone ? 'text-emerald-400' : 'text-[#475569]'}`}>
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-px mx-3 transition-all ${step > s.id ? 'bg-emerald-500/50' : 'bg-[#1e293b]'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Card */}
          <div className="bg-[#111827] border border-[#1e293b] rounded-2xl p-8">
            {/* Step 1 — Account Type */}
            {step === 1 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Choose Account Type</h2>
                <p className="text-[#64748b] text-sm mb-8">Select the type of investor account you want to create.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { type: 'individual' as const, icon: User, title: 'Individual Investor', desc: 'Personal accredited investor account. Suitable for high-net-worth individuals.' },
                    { type: 'corporate' as const, icon: Building2, title: 'Corporate / Institutional', desc: 'Entity account for family offices, funds, and institutional investors.' },
                  ].map(({ type, icon: Icon, title, desc }) => (
                    <button key={type} onClick={() => update('accountType', type)}
                      className={`p-5 rounded-xl border text-left transition-all hover:-translate-y-0.5 ${
                        form.accountType === type
                          ? 'border-emerald-500/50 bg-emerald-900/20' :'border-[#1e293b] bg-[#1a2236] hover:border-[#334155]'
                      }`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                        form.accountType === type ? 'bg-emerald-900/40 border border-emerald-500/30' : 'bg-[#111827] border border-[#1e293b]'
                      }`}>
                        <Icon className={`w-5 h-5 ${form.accountType === type ? 'text-emerald-400' : 'text-[#64748b]'}`} />
                      </div>
                      <div className={`font-semibold mb-1 ${form.accountType === type ? 'text-white' : 'text-[#94a3b8]'}`}>{title}</div>
                      <div className="text-xs text-[#64748b] leading-relaxed">{desc}</div>
                      {form.accountType === type && (
                        <div className="mt-3 flex items-center gap-1 text-xs text-emerald-400">
                          <CheckCircle className="w-3.5 h-3.5" /> Selected
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2 — Personal Details */}
            {step === 2 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Personal Details</h2>
                <p className="text-[#64748b] text-sm mb-8">This information will be used for KYC verification.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <InputField label="First Name" value={form.firstName} onChange={v => update('firstName', v)} placeholder="Khalid" required />
                  <InputField label="Last Name" value={form.lastName} onChange={v => update('lastName', v)} placeholder="Al-Rashidi" required />
                  <div className="sm:col-span-2">
                    <InputField label="Email Address" type="email" value={form.email} onChange={v => update('email', v)} placeholder="khalid@example.com" required />
                  </div>
                  <InputField label="Phone Number" type="tel" value={form.phone} onChange={v => update('phone', v)} placeholder="+971 50 123 4567" />
                  <div>
                    <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-2">Nationality <span className="text-red-400">*</span></label>
                    <select value={form.nationality} onChange={e => update('nationality', e.target.value)}
                      className="w-full bg-[#1a2236] border border-[#1e293b] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 transition-colors">
                      <option value="">Select nationality</option>
                      {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <InputField label="Date of Birth" type="date" value={form.dateOfBirth} onChange={v => update('dateOfBirth', v)} />
                </div>
              </div>
            )}

            {/* Step 3 — Investor Profile */}
            {step === 3 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Investor Profile</h2>
                <p className="text-[#64748b] text-sm mb-8">Required for suitability assessment under DFSA regulations.</p>
                <div className="space-y-5">
                  <SelectField label="Estimated Net Worth" value={form.netWorth} onChange={v => update('netWorth', v)} options={NET_WORTH_OPTIONS} placeholder="Select range" required />
                  <SelectField label="Annual Income" value={form.annualIncome} onChange={v => update('annualIncome', v)} options={INCOME_OPTIONS} placeholder="Select range" required />
                  <SelectField label="Investment Experience" value={form.investmentExperience} onChange={v => update('investmentExperience', v)} options={EXPERIENCE_OPTIONS} placeholder="Select experience" />
                  <div>
                    <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-3">Risk Tolerance <span className="text-red-400">*</span></label>
                    <div className="flex flex-wrap gap-2">
                      {RISK_OPTIONS.map(r => (
                        <button key={r} onClick={() => update('riskTolerance', r)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                            form.riskTolerance === r
                              ? 'bg-emerald-900/30 border-emerald-500/50 text-emerald-400' :'bg-[#1a2236] border-[#1e293b] text-[#64748b] hover:border-[#334155] hover:text-[#94a3b8]'
                          }`}>
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4 — Secure Access */}
            {step === 4 && (
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Create Secure Access</h2>
                <p className="text-[#64748b] text-sm mb-8">Set a strong password for your CrowdVault account.</p>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-900/20 border border-red-800/40 text-sm text-red-400 mb-4">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-2">Password <span className="text-red-400">*</span></label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={form.password}
                        onChange={e => update('password', e.target.value)}
                        placeholder="Minimum 8 characters"
                        className="w-full bg-[#1a2236] border border-[#1e293b] rounded-lg px-3 py-2.5 text-sm text-white pr-10 focus:outline-none focus:border-emerald-500/50 transition-colors placeholder:text-[#475569]" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#94a3b8]">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {form.password && (
                      <div className="mt-2 flex gap-1 items-center">
                        {[8, 12, 16].map(len => (
                          <div key={len} className={`h-1 flex-1 rounded-full transition-all ${
                            form.password.length >= len ? 'bg-emerald-500' : 'bg-[#1e293b]'
                          }`} />
                        ))}
                        <span className="text-xs text-[#64748b] ml-2">
                          {form.password.length < 8 ? 'Too short' : form.password.length < 12 ? 'Fair' : 'Strong'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-2">Confirm Password <span className="text-red-400">*</span></label>
                    <div className="relative">
                      <input type={showConfirm ? 'text' : 'password'} value={form.confirmPassword}
                        onChange={e => update('confirmPassword', e.target.value)}
                        placeholder="Re-enter password"
                        className={`w-full bg-[#1a2236] border rounded-lg px-3 py-2.5 text-sm text-white pr-10 focus:outline-none transition-colors placeholder:text-[#475569] ${
                          form.confirmPassword && form.password !== form.confirmPassword ? 'border-red-500/50' : 'border-[#1e293b] focus:border-emerald-500/50'
                        }`} />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#94a3b8]">
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {form.confirmPassword && form.password !== form.confirmPassword && (
                      <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                    )}
                  </div>
                </div>
                <div className="space-y-3 p-4 rounded-xl bg-[#1a2236] border border-[#1e293b]">
                  {[
                    { key: 'agreeTerms' as const, label: 'I agree to the Terms of Service and Privacy Policy' },
                    { key: 'agreeRisk' as const, label: 'I acknowledge that investing involves risk and I may lose capital' },
                  ].map(({ key, label }) => (
                    <label key={key} className="flex items-start gap-3 cursor-pointer">
                      <div onClick={() => update(key, !form[key])}
                        className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 border transition-all ${
                          form[key] ? 'bg-emerald-500 border-emerald-500' : 'bg-[#111827] border-[#334155]'
                        }`}>
                        {form[key] && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm text-[#94a3b8]">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1e293b]">
              {step > 1 ? (
                <button onClick={() => setStep(s => s - 1)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#1e293b] hover:border-[#334155] text-[#94a3b8] hover:text-white rounded-xl transition-all text-sm">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              ) : <div />}
              {step < 4 ? (
                <button onClick={() => setStep(s => s + 1)} disabled={!canProceed()}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all text-sm">
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={!canProceed() || loading}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all text-sm">
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Submit Application <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-[#475569] mt-6">
            By continuing, you confirm you are an accredited investor as defined under applicable regulations.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function InputField({ label, value, onChange, placeholder, type = 'text', required }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#1a2236] border border-[#1e293b] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 transition-colors placeholder:text-[#475569]" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, required }: {
  label: string; value: string; onChange: (v: string) => void;
  options: string[]; placeholder?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full bg-[#1a2236] border border-[#1e293b] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500/50 transition-colors">
        <option value="">{placeholder || 'Select...'}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
